import { IExtractedField } from 'types/db/ExtractedProForma';
import { DEVELOPMENT_SOURCES_PREFIX, DEVELOPMENT_USES_PREFIX } from 'views/ExtractedProForma/constants';
import { RowValue } from '../../ExecutiveSummaryGenericTable/types';
import { DevelopmentDynamicRow, DevelopmentResolver } from '../../layout/development';
import {
    buildFieldMap, derivedCell, displayValue, ExtractedFieldKey, extractedCell, FieldMap, getField,
    keysWithPrefix, numericValue, rowId,
} from './cells';
import { developmentPercentOfTotal, developmentPerBuiltSf, developmentPerUnit } from './formulas';

const TOTAL_USES_KEY = 'development.total_uses';
const TOTAL_SOURCES_KEY = 'development.total_sources';

const dynamicRow = (fieldMap: FieldMap, key: ExtractedFieldKey, totalKey: ExtractedFieldKey): DevelopmentDynamicRow[] => {
    const field = getField(fieldMap, key);
    if (!field) return [];

    return [{
        id: rowId(key),
        label: field.label,
        cells: [
            derivedCell(() => developmentPercentOfTotal(fieldMap, key, totalKey), 'percentage')(),
            derivedCell(() => developmentPerBuiltSf(fieldMap, key), 'currency-cents')(),
            derivedCell(() => developmentPerUnit(fieldMap, key), 'currency')(),
            { value: displayValue(field), format: 'none' },
        ],
    }];
};

const totalPercentCell = (fieldMap: FieldMap, totalKey: ExtractedFieldKey) => (): RowValue => (
    numericValue(getField(fieldMap, totalKey)) !== undefined ? { value: 1, format: 'percentage' } : { value: '-', format: 'none' }
);

export const extractedDevelopmentResolver = (fields: IExtractedField[]): DevelopmentResolver => {
    const fieldMap = buildFieldMap(fields);
    const usesRows = () => keysWithPrefix(fieldMap, DEVELOPMENT_USES_PREFIX).flatMap(key => dynamicRow(fieldMap, key, TOTAL_USES_KEY));
    const sourcesRows = () => (
        keysWithPrefix(fieldMap, DEVELOPMENT_SOURCES_PREFIX).flatMap(key => dynamicRow(fieldMap, key, TOTAL_SOURCES_KEY))
    );

    return {
        hasUses: () => fieldMap.has(TOTAL_USES_KEY) || usesRows().length > 0,
        hasSources: () => fieldMap.has(TOTAL_SOURCES_KEY) || sourcesRows().length > 0,
        useGroups: () => {
            const rows = usesRows();
            return rows.length > 0 ? [{ id: 'uses-section-root', title: 'Uses', rows }] : [];
        },
        sources: sourcesRows,

        'totalUses.percent': totalPercentCell(fieldMap, TOTAL_USES_KEY),
        'totalUses.perBuiltSf': derivedCell(() => developmentPerBuiltSf(fieldMap, TOTAL_USES_KEY), 'currency-cents'),
        'totalUses.perUnit': derivedCell(() => developmentPerUnit(fieldMap, TOTAL_USES_KEY), 'currency'),
        'totalUses.amount': extractedCell(fieldMap, TOTAL_USES_KEY),

        'totalSources.percent': totalPercentCell(fieldMap, TOTAL_SOURCES_KEY),
        'totalSources.perBuiltSf': derivedCell(() => developmentPerBuiltSf(fieldMap, TOTAL_SOURCES_KEY), 'currency-cents'),
        'totalSources.perUnit': derivedCell(() => developmentPerUnit(fieldMap, TOTAL_SOURCES_KEY), 'currency'),
        'totalSources.amount': extractedCell(fieldMap, TOTAL_SOURCES_KEY),
    };
};
